import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Database, BookOpen, MessageSquare, Mic, Globe, Languages,
  Plus, Edit, Trash2, Save, X, Search, RefreshCw, Download, Upload,
  ChevronLeft, ChevronRight, Filter, Eye, Volume2, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Collection configs
const COLLECTIONS = [
  { id: 'aviation_vocabulary', name: 'Havacılık Kelimeleri', icon: Globe, color: 'from-blue-500 to-cyan-500' },
  { id: 'grammar_questions', name: 'Gramer Soruları', icon: BookOpen, color: 'from-purple-500 to-pink-500' },
  { id: 'daily_challenge_questions', name: 'Günlük Challenge', icon: AlertCircle, color: 'from-amber-500 to-orange-500' },
  { id: 'dialogues', name: 'Diyaloglar', icon: MessageSquare, color: 'from-green-500 to-emerald-500' },
  { id: 'interview_questions', name: 'Mülakat Soruları', icon: Mic, color: 'from-red-500 to-rose-500' },
  { id: 'announcements', name: 'PA Anonsları', icon: Volume2, color: 'from-indigo-500 to-violet-500' },
  { id: 'icao_alphabet', name: 'ICAO Alfabesi', icon: Languages, color: 'from-teal-500 to-cyan-500' },
];

export default function ContentManager() {
  const [stats, setStats] = useState({});
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [categories, setCategories] = useState({ categories: [], levels: [], topics: [] });
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({});
  
  const LIMIT = 20;

  // Load stats on mount
  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/content/stats`, { withCredentials: true });
      setStats(res.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadItems = useCallback(async () => {
    if (!selectedCollection) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: page * LIMIT,
        limit: LIMIT,
        ...(search && { search }),
        ...(filterLevel && { level: filterLevel }),
        ...(filterCategory && { category: filterCategory })
      });
      
      const res = await axios.get(`${API}/admin/content/${selectedCollection.id}?${params}`, { withCredentials: true });
      setItems(res.data.items);
      setTotalItems(res.data.total);
    } catch (error) {
      toast.error('İçerikler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [selectedCollection, page, search, filterLevel, filterCategory]);

  const loadCategories = async () => {
    if (!selectedCollection) return;
    try {
      const res = await axios.get(`${API}/admin/content/${selectedCollection.id}/categories`, { withCredentials: true });
      setCategories(res.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  useEffect(() => {
    if (selectedCollection) {
      loadItems();
      loadCategories();
    }
  }, [selectedCollection, loadItems]);

  const handleSelectCollection = (col) => {
    setSelectedCollection(col);
    setPage(0);
    setSearch('');
    setFilterLevel('');
    setFilterCategory('');
    setNewItem(getEmptyItem(col.id));
  };

  const getEmptyItem = (collectionId) => {
    switch (collectionId) {
      case 'aviation_vocabulary':
        return { word: '', translation: '', pronunciation: '', category: '', level: 'A1', example: '', example_tr: '' };
      case 'grammar_questions':
        return { question: '', options: ['', '', '', ''], correct: 0, level: 'A1', topic: 'present_simple', explanation_tr: '', explanation_en: '' };
      case 'daily_challenge_questions':
        return { question: '', options: ['', '', '', ''], correct: 0, level: 'A1', category: 'vocabulary', explanation_tr: '' };
      case 'dialogues':
        return { title: '', title_en: '', level: 'A1', category: 'greeting', dialogue: [] };
      case 'interview_questions':
        return { question: '', level: 'A1', category: 'personal', sample_answer: '' };
      case 'announcements':
        return { title: '', title_en: '', category: 'boarding', text: '', text_tr: '' };
      case 'icao_alphabet':
        return { letter: '', word: '', pronunciation: '' };
      default:
        return {};
    }
  };

  const handleAddItem = async () => {
    try {
      await axios.post(`${API}/admin/content/${selectedCollection.id}`, newItem, { withCredentials: true });
      toast.success('İçerik eklendi');
      setShowAddModal(false);
      setNewItem(getEmptyItem(selectedCollection.id));
      loadItems();
      loadStats();
    } catch (error) {
      toast.error('Eklenemedi: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateItem = async () => {
    try {
      const itemId = editingItem.id || editingItem.word || editingItem.question || editingItem.letter;
      await axios.put(`${API}/admin/content/${selectedCollection.id}/${itemId}`, editingItem, { withCredentials: true });
      toast.success('İçerik güncellendi');
      setShowEditModal(false);
      setEditingItem(null);
      loadItems();
    } catch (error) {
      toast.error('Güncellenemedi: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm('Bu içeriği silmek istediğinizden emin misiniz?')) return;
    try {
      const itemId = item.id || item.word || item.question || item.letter;
      await axios.delete(`${API}/admin/content/${selectedCollection.id}/${itemId}`, { withCredentials: true });
      toast.success('İçerik silindi');
      loadItems();
      loadStats();
    } catch (error) {
      toast.error('Silinemedi: ' + (error.response?.data?.detail || error.message));
    }
  };

  const renderCollectionGrid = () => (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {COLLECTIONS.map((col) => (
        <Card
          key={col.id}
          className="hover:shadow-lg cursor-pointer group transition-all hover:-translate-y-1"
          onClick={() => handleSelectCollection(col)}
        >
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 bg-gradient-to-br ${col.color} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                <col.icon className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-800">{col.name}</h3>
                <p className="text-2xl font-black text-gray-600">{stats[col.id] || 0}</p>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button variant="outline" size="sm" className="flex-1">
                <Eye className="w-4 h-4 mr-1" /> Görüntüle
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1"
                onClick={(e) => { e.stopPropagation(); handleSelectCollection(col); setShowAddModal(true); }}
              >
                <Plus className="w-4 h-4 mr-1" /> Ekle
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const renderItemsList = () => (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => setSelectedCollection(null)}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Geri
          </Button>
          <div className={`w-10 h-10 bg-gradient-to-br ${selectedCollection.color} rounded-lg flex items-center justify-center`}>
            <selectedCollection.icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold">{selectedCollection.name}</h2>
            <p className="text-sm text-gray-500">{totalItems} içerik</p>
          </div>
        </div>
        <Button onClick={() => { setNewItem(getEmptyItem(selectedCollection.id)); setShowAddModal(true); }} className="gap-2 bg-gradient-to-r from-purple-500 to-pink-500">
          <Plus className="w-4 h-4" /> Yeni Ekle
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Ara..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="pl-9"
          />
        </div>
        {categories.levels?.length > 0 && (
          <select
            value={filterLevel}
            onChange={(e) => { setFilterLevel(e.target.value); setPage(0); }}
            className="px-3 py-2 border rounded-md bg-white min-w-[120px]"
          >
            <option value="">Tüm Seviyeler</option>
            {categories.levels.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        )}
        {categories.categories?.length > 0 && (
          <select
            value={filterCategory}
            onChange={(e) => { setFilterCategory(e.target.value); setPage(0); }}
            className="px-3 py-2 border rounded-md bg-white min-w-[150px]"
          >
            <option value="">Tüm Kategoriler</option>
            {categories.categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <Button variant="outline" onClick={loadItems}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Items Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Database className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>İçerik bulunamadı</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    {renderTableHeaders()}
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.map((item, idx) => (
                    <tr key={item.id || idx} className="hover:bg-gray-50">
                      {renderTableRow(item)}
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => { setEditingItem({...item}); setShowEditModal(true); }}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteItem(item)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalItems > LIMIT && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            {page * LIMIT + 1} - {Math.min((page + 1) * LIMIT, totalItems)} / {totalItems}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" disabled={(page + 1) * LIMIT >= totalItems} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  const renderTableHeaders = () => {
    switch (selectedCollection?.id) {
      case 'aviation_vocabulary':
        return (
          <>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kelime</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Çeviri</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seviye</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategori</th>
          </>
        );
      case 'grammar_questions':
      case 'daily_challenge_questions':
        return (
          <>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Soru</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seviye</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Konu</th>
          </>
        );
      case 'dialogues':
        return (
          <>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Başlık</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seviye</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategori</th>
          </>
        );
      case 'interview_questions':
        return (
          <>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Soru</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seviye</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategori</th>
          </>
        );
      case 'announcements':
        return (
          <>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Başlık</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategori</th>
          </>
        );
      case 'icao_alphabet':
        return (
          <>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Harf</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kod</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Telaffuz</th>
          </>
        );
      default:
        return <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">İçerik</th>;
    }
  };

  const renderTableRow = (item) => {
    switch (selectedCollection?.id) {
      case 'aviation_vocabulary':
        return (
          <>
            <td className="px-4 py-3 font-medium">{item.word}</td>
            <td className="px-4 py-3 text-gray-600">{item.translation}</td>
            <td className="px-4 py-3"><Badge variant="outline">{item.level}</Badge></td>
            <td className="px-4 py-3"><Badge className="bg-blue-100 text-blue-700">{item.category}</Badge></td>
          </>
        );
      case 'grammar_questions':
      case 'daily_challenge_questions':
        return (
          <>
            <td className="px-4 py-3 font-medium max-w-md truncate">{item.question}</td>
            <td className="px-4 py-3"><Badge variant="outline">{item.level}</Badge></td>
            <td className="px-4 py-3"><Badge className="bg-purple-100 text-purple-700">{item.topic || item.category}</Badge></td>
          </>
        );
      case 'dialogues':
        return (
          <>
            <td className="px-4 py-3 font-medium">{item.title}</td>
            <td className="px-4 py-3"><Badge variant="outline">{item.level}</Badge></td>
            <td className="px-4 py-3"><Badge className="bg-green-100 text-green-700">{item.category}</Badge></td>
          </>
        );
      case 'interview_questions':
        return (
          <>
            <td className="px-4 py-3 font-medium max-w-md truncate">{item.question}</td>
            <td className="px-4 py-3"><Badge variant="outline">{item.level}</Badge></td>
            <td className="px-4 py-3"><Badge className="bg-red-100 text-red-700">{item.category}</Badge></td>
          </>
        );
      case 'announcements':
        return (
          <>
            <td className="px-4 py-3 font-medium">{item.title}</td>
            <td className="px-4 py-3"><Badge className="bg-indigo-100 text-indigo-700">{item.category}</Badge></td>
          </>
        );
      case 'icao_alphabet':
        return (
          <>
            <td className="px-4 py-3 font-bold text-xl">{item.letter}</td>
            <td className="px-4 py-3 font-medium">{item.word}</td>
            <td className="px-4 py-3 text-gray-500">{item.pronunciation}</td>
          </>
        );
      default:
        return <td className="px-4 py-3">{JSON.stringify(item).slice(0, 100)}...</td>;
    }
  };

  const renderFormFields = (item, setItem) => {
    switch (selectedCollection?.id) {
      case 'aviation_vocabulary':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">İngilizce Kelime *</label>
                <Input value={item.word || ''} onChange={(e) => setItem({...item, word: e.target.value})} placeholder="aircraft" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Türkçe Çeviri *</label>
                <Input value={item.translation || ''} onChange={(e) => setItem({...item, translation: e.target.value})} placeholder="uçak" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Telaffuz</label>
                <Input value={item.pronunciation || ''} onChange={(e) => setItem({...item, pronunciation: e.target.value})} placeholder="/ˈeəkrɑːft/" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Seviye</label>
                <select value={item.level || 'A1'} onChange={(e) => setItem({...item, level: e.target.value})} className="w-full px-3 py-2 border rounded-md">
                  {['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Kategori</label>
              <Input value={item.category || ''} onChange={(e) => setItem({...item, category: e.target.value})} placeholder="aircraft_parts, cabin, safety, service..." />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Örnek Cümle (İngilizce)</label>
              <Input value={item.example || ''} onChange={(e) => setItem({...item, example: e.target.value})} placeholder="The aircraft is ready for boarding." />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Örnek Cümle (Türkçe)</label>
              <Input value={item.example_tr || ''} onChange={(e) => setItem({...item, example_tr: e.target.value})} placeholder="Uçak binişe hazır." />
            </div>
          </div>
        );
      
      case 'grammar_questions':
      case 'daily_challenge_questions':
        return (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Soru *</label>
              <Input value={item.question || ''} onChange={(e) => setItem({...item, question: e.target.value})} placeholder="I ___ a flight attendant." />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Seçenekler (4 adet) *</label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {[0, 1, 2, 3].map(i => (
                  <Input
                    key={i}
                    value={item.options?.[i] || ''}
                    onChange={(e) => {
                      const opts = [...(item.options || ['', '', '', ''])];
                      opts[i] = e.target.value;
                      setItem({...item, options: opts});
                    }}
                    placeholder={`Seçenek ${i + 1}`}
                  />
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Doğru Cevap (0-3)</label>
                <Input type="number" min="0" max="3" value={item.correct ?? 0} onChange={(e) => setItem({...item, correct: parseInt(e.target.value)})} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Seviye</label>
                <select value={item.level || 'A1'} onChange={(e) => setItem({...item, level: e.target.value})} className="w-full px-3 py-2 border rounded-md">
                  {['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Konu</label>
                <Input value={item.topic || item.category || ''} onChange={(e) => setItem({...item, topic: e.target.value, category: e.target.value})} placeholder="present_simple" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Açıklama (Türkçe)</label>
              <Input value={item.explanation_tr || ''} onChange={(e) => setItem({...item, explanation_tr: e.target.value})} placeholder="'I' ile 'am' kullanılır." />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Açıklama (İngilizce)</label>
              <Input value={item.explanation_en || ''} onChange={(e) => setItem({...item, explanation_en: e.target.value})} placeholder="'Am' is used with 'I'." />
            </div>
          </div>
        );
      
      case 'interview_questions':
        return (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Mülakat Sorusu *</label>
              <Input value={item.question || ''} onChange={(e) => setItem({...item, question: e.target.value})} placeholder="Tell me about yourself." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Seviye</label>
                <select value={item.level || 'A1'} onChange={(e) => setItem({...item, level: e.target.value})} className="w-full px-3 py-2 border rounded-md">
                  {['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Kategori</label>
                <select value={item.category || 'personal'} onChange={(e) => setItem({...item, category: e.target.value})} className="w-full px-3 py-2 border rounded-md">
                  {['personal', 'motivation', 'skills', 'teamwork', 'problem_solving', 'experience', 'safety', 'service'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Örnek Cevap</label>
              <textarea
                value={item.sample_answer || ''}
                onChange={(e) => setItem({...item, sample_answer: e.target.value})}
                className="w-full px-3 py-2 border rounded-md h-24"
                placeholder="My name is ... I am ... years old..."
              />
            </div>
          </div>
        );
      
      case 'announcements':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Başlık (Türkçe) *</label>
                <Input value={item.title || ''} onChange={(e) => setItem({...item, title: e.target.value})} placeholder="Hoş Geldiniz" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Başlık (İngilizce)</label>
                <Input value={item.title_en || ''} onChange={(e) => setItem({...item, title_en: e.target.value})} placeholder="Welcome Aboard" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Kategori</label>
              <select value={item.category || 'boarding'} onChange={(e) => setItem({...item, category: e.target.value})} className="w-full px-3 py-2 border rounded-md">
                {['boarding', 'welcome', 'safety', 'service', 'turbulence', 'landing', 'farewell'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Anons Metni (İngilizce) *</label>
              <textarea
                value={item.text || ''}
                onChange={(e) => setItem({...item, text: e.target.value})}
                className="w-full px-3 py-2 border rounded-md h-24"
                placeholder="Ladies and gentlemen, welcome aboard..."
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Anons Metni (Türkçe) *</label>
              <textarea
                value={item.text_tr || ''}
                onChange={(e) => setItem({...item, text_tr: e.target.value})}
                className="w-full px-3 py-2 border rounded-md h-24"
                placeholder="Bayanlar ve baylar, uçağımıza hoş geldiniz..."
              />
            </div>
          </div>
        );
      
      case 'icao_alphabet':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Harf *</label>
                <Input value={item.letter || ''} onChange={(e) => setItem({...item, letter: e.target.value.toUpperCase()})} placeholder="A" maxLength={1} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">ICAO Kodu *</label>
                <Input value={item.word || ''} onChange={(e) => setItem({...item, word: e.target.value})} placeholder="Alpha" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Telaffuz</label>
                <Input value={item.pronunciation || ''} onChange={(e) => setItem({...item, pronunciation: e.target.value})} placeholder="AL-FAH" />
              </div>
            </div>
          </div>
        );
      
      default:
        return <p className="text-gray-500">Bu koleksiyon için form tanımlanmamış.</p>;
    }
  };

  // Add Modal
  const renderAddModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between border-b">
          <CardTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" /> Yeni {selectedCollection?.name} Ekle
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}><X className="w-4 h-4" /></Button>
        </CardHeader>
        <CardContent className="p-6 overflow-y-auto max-h-[70vh]">
          {renderFormFields(newItem, setNewItem)}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowAddModal(false)}>İptal</Button>
            <Button onClick={handleAddItem} className="gap-2 bg-gradient-to-r from-green-500 to-emerald-500">
              <Save className="w-4 h-4" /> Kaydet
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Edit Modal
  const renderEditModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between border-b">
          <CardTitle className="flex items-center gap-2">
            <Edit className="w-5 h-5" /> {selectedCollection?.name} Düzenle
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => { setShowEditModal(false); setEditingItem(null); }}><X className="w-4 h-4" /></Button>
        </CardHeader>
        <CardContent className="p-6 overflow-y-auto max-h-[70vh]">
          {editingItem && renderFormFields(editingItem, setEditingItem)}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button variant="outline" onClick={() => { setShowEditModal(false); setEditingItem(null); }}>İptal</Button>
            <Button onClick={handleUpdateItem} className="gap-2 bg-gradient-to-r from-blue-500 to-indigo-500">
              <Save className="w-4 h-4" /> Güncelle
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-black text-gray-800">MongoDB İçerik Yönetimi</h2>
          <p className="text-gray-600">Tüm eğitim içeriklerini yönetin - AI maliyeti sıfır!</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadStats} className="gap-2">
            <RefreshCw className="w-4 h-4" /> Yenile
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-sm text-blue-600 font-medium">Toplam Kelime</p>
            <p className="text-2xl font-black text-blue-700">{stats.aviation_vocabulary || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
          <CardContent className="p-4">
            <p className="text-sm text-purple-600 font-medium">Gramer Soruları</p>
            <p className="text-2xl font-black text-purple-700">{stats.grammar_questions || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-4">
            <p className="text-sm text-green-600 font-medium">Diyaloglar</p>
            <p className="text-2xl font-black text-green-700">{stats.dialogues || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="p-4">
            <p className="text-sm text-amber-600 font-medium">Challenge Soruları</p>
            <p className="text-2xl font-black text-amber-700">{stats.daily_challenge_questions || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      {selectedCollection ? renderItemsList() : renderCollectionGrid()}

      {/* Modals */}
      {showAddModal && renderAddModal()}
      {showEditModal && editingItem && renderEditModal()}
    </div>
  );
}
